/**
 * @fileoverview Authentication Header Component
 * 
 * This component displays the header section for authentication-related pages
 * in the Heidrick & Struggles Leader Profile application. It includes the
 * company logo or title, the "Leader Portal" text, and a profile dropdown
 * menu for user actions like viewing "About" and "Log Out".
 */

import React, { useContext, useState } from "react";
import { List, Dropdown, Icon, Avatar, IconName, Navbar, NavbarContent } from "@eightfold.ai/octuple";
import { useNavigate } from "react-router";
import { useOktaAuth } from '@okta/okta-react';
import { globalContext } from "../store";

// Options for the profile dropdown menu
const sampleList: string[] = ['About', 'Log Out'];

/**
 * Overlay Component for Profile Dropdown
 * 
 * Renders the dropdown menu content with navigation options.
 * Handles click events for the "About" and "Log Out" actions.
 * 
 * @param {Object} props - Component props including setLoader function
 * @returns {JSX.Element} The rendered dropdown menu overlay
 */
const Overlay = (props: any) => {
  const navigate = useNavigate();
  const { oktaAuth } = useOktaAuth();

  /**
   * Handles navigation or logout based on the clicked menu item
   * 
   * @param {Event} e - Click event object
   */
  const gotoWelcome = (e: any) => {
    if (e.target.innerText === 'About') {
      navigate('/');
    } else if (e.target.innerText === 'Log Out') {
      sessionStorage.clear();
      props.setLoader(true);
      if (oktaAuth) {
        oktaAuth.signOut({
          postLogoutRedirectUri: window.location.origin
        });
      }
    }
  }
  return (
    <List
      items={sampleList}
      layout="vertical"
      classNames="list"
      onClick={gotoWelcome}
      renderItem={(item: string) => <div style={{ margin: '8px', cursor: 'pointer' }}>{item}</div>}
    />
  );
};

/**
 * ProfileDropdown Component
 * 
 * Renders a user profile dropdown with the user's initials in an avatar
 * and a dropdown menu with options like "About" and "Log Out".
 * 
 * @param {Object} props - Component props including setLoader function
 * @returns {JSX.Element} The rendered profile dropdown component
 */
const ProfileDropdown = (props: any) => {
  const [, setVisibility] = useState(false);
  const { globalState } = useContext(globalContext);

  /**
   * Generates the user's initials from first and last name
   * to display in the avatar component
   * 
   * @returns {string|undefined} User's initials (uppercase) or undefined if names not available
   */
  const profileName = () => {
    if (
      globalState?.leaderData?.firstName &&
      globalState?.leaderData?.lastName
    ) {
      return (
        globalState?.leaderData?.firstName?.[0]?.toUpperCase() +
        globalState?.leaderData?.lastName?.[0]?.toUpperCase()
      );
    }
  };

  return (
    <Dropdown
      onVisibleChange={(isVisible) => setVisibility(isVisible)}
      overlay={<Overlay setLoader={props.setLoader} />}
      dropdownStyle={{ color: '#212121', top: '79px' }}
      classNames="dropdownclass"
    >
      <div className="divclass" style={{ display: 'flex', top: '79px' }}>
        <Avatar
          classNames="my-avatar-class"
          data-test-id="my-avatar-test-id"
          fontSize="18px"
          size="40px"
          style={{}}
          theme="grey"
          type="round"
        >
          {profileName()}
        </Avatar>
        <Icon path={IconName.mdiMenuDown} />
      </div>
    </Dropdown>
  );
};

/**
 * AuthHeader Component
 * 
 * Main header component for authenticated pages that displays branding and user menu.
 * Conditionally shows either a text title or a logo image based on props.
 * Always displays "Leader Portal" text and user profile dropdown.
 * 
 * @param {Object} props - Component props including:
 *   - title: Optional title text to display
 *   - headerLogo: Optional logo image URL
 *   - setLoader: Function to control loading state
 * @returns {JSX.Element} The rendered authentication header
 */
const AuthHeader = (props: any) => {
  return (
    <div className="authHeader">
      <Navbar classNames="authHeaderLogo">
        {/* Left side content: Logo/Title and Portal name */}
        <NavbarContent>
          {/* Conditional rendering: Show title text or logo image */}
          {(props?.title && (props.headerLogo === '' || props.headerLogo === null || props.headerLogo === undefined)) ?
            <h4 className="authHeaderTitle">{props?.title}</h4>
            :
            <div className='headerLogo'>
              <div className="dynamicHeader">
                <img className="dynamicHeaderLogo" alt="header icon" src={props.headerLogo} />
              </div>
              <div className='dividerHeader'></div>
            </div>
          }
          <div className='authDividerHeader'></div>
          <h4 className='headerTitle'>Leader Portal</h4>
        </NavbarContent>

        {/* Right side content: User profile dropdown */}
        <NavbarContent>
          <ProfileDropdown setLoader={props.setLoader} />
        </NavbarContent>
      </Navbar>
    </div>
  );
};

export default AuthHeader;